// The list contract, from the client's side.
//
// Every collection takes the same six query parameters, and a filter is a
// JSON tree of conditions over properties the server names per list. The
// names and operators come from filters.gen.ts, which `mindvm filters`
// writes from the server's own registries — so a property that does not
// exist, or an operator a property does not take, is a type error here
// rather than a 400 in production.

import type { ListName, Registries } from './filters.gen';

type Props<L extends ListName> = keyof Registries[L] & string;
type Field<L extends ListName, P extends Props<L>> = Registries[L][P];
type Ops<L extends ListName, P extends Props<L>> = Field<L, P> extends { operators: readonly (infer O)[] } ? O : never;
type Values<L extends ListName, P extends Props<L>> = Field<L, P> extends { values: readonly (infer V)[] } ? V : string | number | boolean;

export interface Condition<L extends ListName, P extends Props<L> = Props<L>> {
  property: P;
  operator: Ops<L, P>;
  value?: Values<L, P> | Values<L, P>[] | null;
}

export interface Group<L extends ListName> {
  op: 'and' | 'or';
  children: Filter<L>[];
}

export type Filter<L extends ListName> = Condition<L> | Group<L>;

/** One condition. The list name is what types the property and operator. */
export function where<L extends ListName, P extends Props<L>>(
  _list: L,
  property: P,
  operator: Ops<L, P>,
  value?: Values<L, P> | Values<L, P>[] | null,
): Condition<L, P> {
  return value === undefined ? { property, operator } : { property, operator, value };
}

export function all<L extends ListName>(...children: Filter<L>[]): Group<L> {
  return { op: 'and', children };
}

export function any<L extends ListName>(...children: Filter<L>[]): Group<L> {
  return { op: 'or', children };
}

/** The wire shape of a list request. */
export interface ListQuery {
  filter?: string;
  search?: string;
  sort?: string;
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface ListOptions<L extends ListName> {
  /** One filter, or several which are AND-ed. */
  where?: Filter<L> | Filter<L>[];
  search?: string;
  sort?: string;
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/** Builds the query for a list call: `listX({ query: listQuery('x', {...}) })`. */
export function listQuery<L extends ListName>(_list: L, opts: ListOptions<L> = {}): ListQuery {
  const out: ListQuery = {};
  if (opts.where) {
    const filter = Array.isArray(opts.where) ? (opts.where.length === 1 ? opts.where[0]! : all(...opts.where)) : opts.where;
    out.filter = JSON.stringify(filter);
  }
  if (opts.search) out.search = opts.search;
  if (opts.sort) out.sort = opts.sort;
  if (opts.sortDir) out.sortDir = opts.sortDir;
  if (opts.limit !== undefined) out.limit = opts.limit;
  if (opts.offset !== undefined) out.offset = opts.offset;
  return out;
}
